/**
 * The report's `solutions-v4` section (#1474, specs/workshop-modules/solutions.md v4, "Report"):
 * Ratkaisut v4's solutions by the vipupiste each was written on, in type order, each with its
 * version, its three scores, what it moves and the problems it takes on; then the solutions
 * together (Kooste): the facilitator's reading when there is one, and the gaps computed from the
 * assessments. The data is the module's board (useChainReportData), without device ids.
 */
import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Section, SectionTitle, SectionSubtitle, Paragraph, SITRA } from './reportStyles';

const TYPE_ORDER = ['konsensus', 'kiistanalainen', 'villi_kortti', 'sokea_piste'];

const List = styled.ul`margin: 12px 0 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 10px;`;
const Item = styled.li`
  padding: 12px 14px; border-radius: 4px; border: 1px solid ${SITRA.border}; background: ${SITRA.white}; color: ${SITRA.textBody};
  .title { font-weight: 600; }
  .meta { margin-top: 4px; font-size: 14px; color: ${SITRA.textMuted}; }
  ul { margin: 6px 0 0; padding-left: 18px; font-size: 15px; }
`;
const Group = styled.div`margin-top: 18px;`;

/** The solutions grouped by the vipupiste they were written on, in type order, those on an earlier run last */
export function reportGroups(pivots, solutions) {
  const assessed = solutions.filter((s) => s.assessment);
  const ordered = TYPE_ORDER.flatMap((type) => pivots.filter((p) => p.tyyppi === type));
  const shown = new Set(ordered.map((p) => p.id));
  const groups = ordered
    .map((p) => ({ id: p.id, title: p.otsikko, solutions: assessed.filter((s) => s.vipupiste?.id === p.id) }))
    .filter((g) => g.solutions.length);
  const earlier = assessed.filter((s) => !shown.has(s.vipupiste?.id));
  return earlier.length ? [...groups, { id: 'earlier', title: null, solutions: earlier }] : groups;
}

export default function ReportSolutionsV4({ data }) {
  const { t } = useTranslation();
  const k = (key, vars) => t(`report.chainSections.solutionsV4.${key}`, vars);
  const { pivots, solutions, kooste } = data;
  const reading = kooste?.reading?.result;
  const gaps = kooste?.gaps;
  return (
    <Section id="solutions-v4">
      <SectionTitle>{t('report.nav.solutionsV4')}</SectionTitle>
      <Paragraph>{k('intro')}</Paragraph>
      {reportGroups(pivots, solutions).map((g) => (
        <Group key={g.id}>
          <SectionSubtitle>{g.title || k('earlier')}</SectionSubtitle>
          <List>
            {g.solutions.map((s) => {
              const sc = s.assessment.scores;
              return (
                <Item key={s.id}>
                  <div className="title">{s.title}</div>
                  {s.body ? <div>{s.body}</div> : null}
                  <div className="meta">
                    {sc ? k('scores', { feasibility: sc.feasibility ?? '–', ambition: sc.ambition ?? '–', effectiveness: sc.effectiveness ?? '–' }) : null}
                    {s.version > 1 ? ` · ${k('version', { version: s.version })}` : ''}
                  </div>
                  {s.assessment.moves?.length ? (
                    <>
                      <div className="meta">{k('moves')}</div>
                      <ul>{s.assessment.moves.map((m) => <li key={m.vipupisteId}>{m.title}: {k(m.direction === 'against' ? 'against' : 'asIntended')}</li>)}</ul>
                    </>
                  ) : null}
                  {s.assessment.problems?.length ? (
                    <>
                      <div className="meta">{k('problems')}</div>
                      <ul>{s.assessment.problems.map((p) => <li key={p.problemId}>{p.title} ({k(p.reach === 'solves' ? 'solves' : 'touches')})</li>)}</ul>
                    </>
                  ) : null}
                </Item>
              );
            })}
          </List>
        </Group>
      ))}

      {reading || gaps ? <SectionSubtitle>{k('kooste')}</SectionSubtitle> : null}
      {reading?.overview ? <Paragraph>{reading.overview}</Paragraph> : null}
      {reading?.tensions?.length ? (
        <>
          <Paragraph><strong>{k('tensions')}</strong></Paragraph>
          <ul>{reading.tensions.map((x, i) => <li key={i}>{x.text}</li>)}</ul>
        </>
      ) : null}
      {gaps?.untouched?.length ? <Paragraph>{k('untouched', { list: gaps.untouched.map((u) => u.title).join(', ') })}</Paragraph> : null}
      {gaps?.problemsLeft?.length ? <Paragraph>{k('problemsLeft', { list: gaps.problemsLeft.map((p) => p.title).join(', ') })}</Paragraph> : null}
    </Section>
  );
}
